import { useState } from 'react'
import { api, errorMessage, useQuery } from '../api.js'
import { Alert, Button, Field, Pill } from './ui.js'

type Servidor = Awaited<ReturnType<typeof api.fleet.servers.query>>[number]
type Pendente = Awaited<ReturnType<typeof api.identity.pendingEnrollments.query>>[number]

/** URL do painel = a propria origem do console (mesmo dominio). */
const painel = () => (typeof window !== 'undefined' ? window.location.origin : '')

function comandoLinux(p: string): string {
  return `curl -fsSL '${p}/agent/download/linux' -o /usr/local/bin/via-agent && chmod +x /usr/local/bin/via-agent && /usr/local/bin/via-agent inscrever -painel '${p}'`
}

function comandoWindows(p: string): string {
  return (
    `$p='${p}';$d="$env:ProgramData\\Via360";New-Item -ItemType Directory -Force $d|Out-Null;` +
    `$b="$d\\via-agent.exe";Invoke-WebRequest "$p/agent/download/windows" -OutFile $b;& $b inscrever -painel $p`
  )
}

/**
 * Conectar um servidor — o caminho de uma tela so.
 *
 * Antes eram tres: cadastrar na Frota, ir em Contas gerar token, voltar
 * aprovar. Agora: escolhe o SO, cola UM comando na maquina, e aprova aqui
 * embaixo — e a aprovacao ja cria o servidor com o que o agente reportou.
 * Nenhum token para digitar, nenhum cadastro previo.
 */
export function ConectarServidor({ onClose }: { onClose: () => void }) {
  const [so, setSo] = useState<'linux' | 'windows'>('linux')
  const [copiado, setCopiado] = useState(false)
  const p = painel()
  const comando = so === 'linux' ? comandoLinux(p) : comandoWindows(p)

  async function copiar() {
    try {
      await navigator.clipboard.writeText(comando)
      setCopiado(true)
      setTimeout(() => setCopiado(false), 2000)
    } catch {
      // Sem permissao de area de transferencia: o usuario seleciona e copia.
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-line bg-panel p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold tracking-tight text-ink">Conectar um servidor</h3>
            <p className="mt-1 text-sm text-ink-2">
              Um comando na máquina, e você aprova aqui. O servidor é criado sozinho — sem token,
              sem cadastro antes.
            </p>
          </div>
          <button onClick={onClose} className="text-ink-3 hover:text-ink" aria-label="Fechar">
            ✕
          </button>
        </div>

        <ol className="mt-6 space-y-6">
          <li>
            <PassoNum n={1} titulo="Escolha o sistema do servidor" />
            <div className="mt-2 flex gap-2">
              {(['linux', 'windows'] as const).map((k) => (
                <button
                  key={k}
                  onClick={() => setSo(k)}
                  className={`rounded-lg border px-4 py-2 text-sm capitalize ${
                    so === k
                      ? 'border-accent bg-accent-soft text-accent'
                      : 'border-line text-ink-2 hover:border-accent'
                  }`}
                >
                  {k}
                </button>
              ))}
            </div>
          </li>

          <li>
            <PassoNum n={2} titulo="Cole este comando na máquina (como root/Administrador)" />
            <p className="mt-1 text-xs text-ink-3">
              Ele baixa o agente e pede a inscrição. Vai mostrar um código e ficar aguardando a sua
              aprovação — deixe a janela aberta.
            </p>
            <div className="mt-2 flex items-start gap-2">
              <pre className="flex-1 overflow-x-auto rounded-lg border border-line bg-ground p-3 font-mono text-[11px] leading-relaxed text-ink">
                {comando}
              </pre>
              <Button onClick={copiar} variant={copiado ? 'primary' : 'default'}>
                {copiado ? 'copiado' : 'copiar'}
              </Button>
            </div>
          </li>

          <li>
            <PassoNum n={3} titulo="Aprove abaixo quando a máquina aparecer" />
            <p className="mt-1 text-xs text-ink-3">
              Confira o hostname e o código com o que apareceu no console, e clique Aprovar. Depois,
              na máquina, rode <span className="font-mono">via-agent rodar</span> (ou instale como
              serviço) para ele ficar no ar sozinho.
            </p>
            <div className="mt-3">
              <PendentesDeAprovacao vazioSuave />
            </div>
          </li>
        </ol>

        <div className="mt-6 flex justify-end">
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </div>
  )
}

function PassoNum({ n, titulo }: { n: number; titulo: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-accent text-xs font-bold text-white">
        {n}
      </span>
      <span className="text-sm font-medium text-ink">{titulo}</span>
    </div>
  )
}

/**
 * Lista de inscrições pendentes com aprovação que já cria o servidor.
 *
 * Reutilizada na aba Contas e no fluxo "Conectar servidor". A opção padrão do
 * seletor é "criar servidor novo" com os dados que o agente reportou; escolher
 * um servidor existente é o caminho da reinstalação.
 */
export function PendentesDeAprovacao({ vazioSuave }: { vazioSuave?: boolean }) {
  const pendentes = useQuery(() => api.identity.pendingEnrollments.query(), [], { pollMs: 8_000 })
  const frota = useQuery(() => api.fleet.servers.query(), [], { pollMs: 15_000 })
  const [escolha, setEscolha] = useState<Record<string, string>>({})
  const [ambiente, setAmbiente] = useState<Record<string, string>>({})
  const [erro, setErro] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)

  const lista: Pendente[] = pendentes.data ?? []
  const servidores: Servidor[] = frota.data ?? []

  if (lista.length === 0) {
    if (vazioSuave) {
      return (
        <p className="rounded-lg border border-dashed border-line px-4 py-6 text-center text-sm text-ink-3">
          Aguardando a máquina aparecer... assim que você rodar o comando, ela surge aqui.
        </p>
      )
    }
    return null
  }

  async function decidir(fn: () => Promise<unknown>) {
    setOcupado(true)
    setErro(null)
    try {
      await fn()
      await Promise.all([pendentes.refetch(), frota.refetch()])
    } catch (e) {
      setErro(errorMessage(e))
    } finally {
      setOcupado(false)
    }
  }

  return (
    <div className="space-y-3">
      {erro && <Alert>{erro}</Alert>}
      {lista.map((p) => {
        const alvo = escolha[p.id] ?? '' // '' = criar servidor novo
        return (
          <div key={p.id} className="rounded-lg border border-line p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-lg tracking-[0.3em] text-accent">{p.code}</span>
              <span className="font-medium text-ink">{p.hostname}</span>
              <Pill tone="neutral">{p.platform}</Pill>
              <span className="font-mono text-xs text-ink-3">{p.ip ?? 'sem IP'}</span>
            </div>
            <div className="mt-3 flex flex-wrap items-end gap-2">
              <Field label="Vincular a">
                <select
                  value={alvo}
                  onChange={(e) => setEscolha((s) => ({ ...s, [p.id]: e.target.value }))}
                  className="rounded-lg border border-line bg-panel px-3 py-2 text-sm text-ink focus:border-accent focus:outline-none"
                >
                  <option value="">➕ Criar servidor novo ({p.hostname})</option>
                  {servidores.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.hostname}
                    </option>
                  ))}
                </select>
              </Field>
              {alvo === '' && (
                <Field label="Ambiente">
                  <select
                    value={ambiente[p.id] ?? 'producao'}
                    onChange={(e) => setAmbiente((s) => ({ ...s, [p.id]: e.target.value }))}
                    className="rounded-lg border border-line bg-panel px-3 py-2 text-sm text-ink focus:border-accent focus:outline-none"
                  >
                    <option value="producao">Produção</option>
                    <option value="homologacao">Homologação</option>
                    <option value="desenvolvimento">Desenvolvimento</option>
                    <option value="outro">Outro</option>
                  </select>
                </Field>
              )}
              <Button
                variant="primary"
                disabled={ocupado}
                onClick={() =>
                  decidir(() =>
                    api.identity.approveEnrollment.mutate({
                      requestId: p.id,
                      serverId: alvo || undefined,
                      environment:
                        alvo === ''
                          ? ((ambiente[p.id] ?? 'producao') as
                              | 'producao'
                              | 'homologacao'
                              | 'desenvolvimento'
                              | 'outro')
                          : undefined,
                    }),
                  )
                }
              >
                Aprovar
              </Button>
              <Button
                variant="ghost"
                disabled={ocupado}
                onClick={() => decidir(() => api.identity.rejectEnrollment.mutate({ requestId: p.id }))}
              >
                Recusar
              </Button>
            </div>
          </div>
        )
      })}
    </div>
  )
}
